import React, { useEffect, useState } from 'react';
import styles from './styles.module.scss';
import InviteStaffModal from './invite_modal';
import StaffProfileModal from './profile_modal';
import OrganizationLogo from 'shared_features/organization_logo';
import SubscriptionSection from 'shared_features/subscription_section';
import Email from 'shared_features/svgs/email';

const StaffView = ({
    currentOrganization,
    staffMembers,
    allStaffMembers,
    pendingInvites,
    loading,
    searchTerm,
    error,
    isUploadingLogo,
    isUploadingPhoto,
    fetchOrganizationRoles,
    updateStaffSearch,
    assignRole,
    removeRole,
    inviteStaff,
    fetchPendingInvites,
    cancelInvite,
    updateOrganizationName,
    uploadOrganizationLogo,
    uploadStaffPhoto,
    clearStaffError,
}) => {
    const [isInviteModalOpen, setIsInviteModalOpen] = useState(false);
    const [isProfileModalOpen, setIsProfileModalOpen] = useState(false);
    const [selectedMember, setSelectedMember] = useState(null);
    const [lastInviteAttempt, setLastInviteAttempt] = useState(null);
    const [lastSaveAttempt, setLastSaveAttempt] = useState(null);
    const [isEditingOrgName, setIsEditingOrgName] = useState(false);
    const [orgNameValue, setOrgNameValue] = useState('');
    const [logoTimestamp, setLogoTimestamp] = useState(() => Date.now());

    // Close modal on successful invite
    useEffect(() => {
        if (lastInviteAttempt && !loading && !error) {
            setIsInviteModalOpen(false);
            setLastInviteAttempt(null);
        }
    }, [loading, error, lastInviteAttempt]);

    // Close profile modal on successful save
    useEffect(() => {
        if (lastSaveAttempt && !loading && !error) {
            setIsProfileModalOpen(false);
            setSelectedMember(null);
            setLastSaveAttempt(null);
        }
    }, [loading, error, lastSaveAttempt]);

    useEffect(() => {
        if (currentOrganization?.id !== undefined) {
            console.log('Fetching roles for org ID:', currentOrganization.id);
            // Skip API call if org ID is 0 (invalid UUID)
            if (currentOrganization.id !== 0) {
                fetchOrganizationRoles(currentOrganization.id);
                fetchPendingInvites(currentOrganization.id);
            } else {
                console.warn('Organization ID is 0, which is not a valid UUID. Skipping API call.');
            }
        }
    }, [currentOrganization?.id, fetchOrganizationRoles, fetchPendingInvites]);

    // Set document title when component mounts
    useEffect(() => {
        document.title = 'Settings - Staff';
    }, []);

    // Update logo timestamp when logo URL changes to force cache bust
    useEffect(() => {
        setLogoTimestamp(Date.now());
    }, [currentOrganization?.logo_url]);

    // Update selectedMember when the corresponding member in allStaffMembers changes (e.g., after photo upload)
    useEffect(() => {
        if (selectedMember && allStaffMembers) {
            const updatedMember = allStaffMembers.find(m => m.account_id === selectedMember.account_id);
            if (updatedMember && updatedMember.photo !== selectedMember.photo) {
                setSelectedMember(updatedMember);
            }
        }
    }, [allStaffMembers]);

    const handleSearchChange = (e) => {
        updateStaffSearch(e.target.value);
    };

    const handleRoleChange = (accountId, formData) => {
        if (currentOrganization?.id) {
            assignRole(
                currentOrganization.id,
                accountId,
                formData.role,
                formData.title,
                formData.bio,
                formData.photo,
                formData.name,
                formData.last_name,
                formData.instagram,
                formData.tiktok,
                formData.facebook,
                formData.twitter
            );
        }
    };

    const handleRowClick = (member) => {
        if (canEditMember(member, currentUserRole)) {
            clearStaffError(); // Clear any existing errors when opening modal
            setSelectedMember(member);
            setIsProfileModalOpen(true);
        }
    };

    const handleProfileSave = (formData) => {
        if (selectedMember && currentOrganization?.id) {
            setLastSaveAttempt(Date.now());
            handleRoleChange(selectedMember.account_id, formData);
            // Don't close modal immediately - let success/failure determine that
        }
    };

    const handleProfileClose = () => {
        clearStaffError(); // Clear any existing errors when closing modal
        setIsProfileModalOpen(false);
        setSelectedMember(null);
        setLastSaveAttempt(null); // Clear save attempt tracker
    };

    const handlePhotoUpload = (file) => {
        if (selectedMember && currentOrganization?.id) {
            uploadStaffPhoto(currentOrganization.id, selectedMember.account_id, file);
        }
    };

    const handleRemoveUser = (accountId) => {
        if (currentOrganization?.id && window.confirm('Are you sure you want to remove this user?')) {
            removeRole(currentOrganization.id, accountId);
        }
    };

    const getInitials = (firstName, lastName) => {
        return `${firstName?.[0] || ''}${lastName?.[0] || ''}`.toUpperCase();
    };

    const handleInviteStaff = (inviteData) => {
        if (currentOrganization?.id) {
            setLastInviteAttempt(Date.now());
            inviteStaff(currentOrganization.id, inviteData);
            // Don't close modal immediately - let success/failure close it
        }
    };

    const handleCancelInvite = (inviteId) => {
        if (currentOrganization?.id && window.confirm('Are you sure you want to cancel this invitation?')) {
            cancelInvite(currentOrganization.id, inviteId);
        }
    };

    const canModifyRole = (memberRole, currentUserRole, isCurrentUser = false) => {
        // Users can demote themselves (except if they're the only owner)
        if (isCurrentUser) {
            if (memberRole === 'OWNER') {
                // Check if they're the only owner
                const ownerCount = allStaffMembers?.filter(member => member.role === 'OWNER').length || 0;
                return ownerCount > 1; // Can only demote if there are other owners
            }
            // Users can only demote themselves, not promote
            // This function is called to check if the dropdown should be enabled
            // The actual role options will be filtered in the dropdown render
            return true;
        }

        // Only owners can modify admin roles, admins can only modify staff
        if (currentUserRole === 'OWNER') return true;
        if (currentUserRole === 'ADMIN') return memberRole === 'STAFF';
        return false;
    };

    const canEditMember = (member, currentUserRole) => {
        // Users can always edit their own profile
        if (member.account?.isCurrentUser) return true;
        // Otherwise use the same logic as role editing
        return canModifyRole(member.role, currentUserRole, false);
    };

    const getAvailableRoles = (memberRole, currentUserRole, isCurrentUser) => {
        const allRoles = [
            { value: 'OWNER', label: 'Owner' },
            { value: 'ADMIN', label: 'Admin' },
            { value: 'STAFF', label: 'Staff' }
        ];

        if (isCurrentUser) {
            // Users can only demote themselves or stay at the same level
            if (memberRole === 'OWNER') {
                return allRoles; // Owners can demote to any role (if not the only owner, checked elsewhere)
            } else if (memberRole === 'ADMIN') {
                return allRoles.filter(role => role.value === 'ADMIN' || role.value === 'STAFF');
            } else {
                return allRoles.filter(role => role.value === 'STAFF');
            }
        } else {
            // Non-self actions
            if (currentUserRole === 'OWNER') {
                return allRoles; // Owners can assign any role
            } else if (currentUserRole === 'ADMIN') {
                return allRoles.filter(role => role.value === 'STAFF'); // Admins can only manage staff
            } else {
                return []; // Staff can't modify others
            }
        }
    };

    const currentUserRole = allStaffMembers?.find(member => member.account?.isCurrentUser)?.role || 'STAFF';

    // Organization name edit handlers
    const handleEditOrgName = () => {
        setOrgNameValue(currentOrganization?.name || '');
        setIsEditingOrgName(true);
    };

    const handleSaveOrgName = () => {
        if (orgNameValue.trim() && currentOrganization?.id) {
            updateOrganizationName(currentOrganization.id, orgNameValue.trim());
            setIsEditingOrgName(false);
        }
    };

    const handleCancelOrgNameEdit = () => {
        setIsEditingOrgName(false);
        setOrgNameValue('');
    };

    const handleOrgNameKeyPress = (e) => {
        if (e.key === 'Enter') {
            handleSaveOrgName();
        } else if (e.key === 'Escape') {
            handleCancelOrgNameEdit();
        }
    };

    console.log('Staff members:', staffMembers);
    console.log('Loading:', loading);

    if (loading) {
        return <div className={styles.container}>Loading...</div>;
    }

    return (
        <div className={styles.container}>
            <div className={styles.settingsSection}>
                {/* Organization name edit section - only visible to owners */}
                {currentUserRole === 'OWNER' && (
                    <div className={styles.organizationDetailsSection}>
                        <div className={styles.organizationNameSection}>
                            <h2>Organization Name</h2>
                            <div className={styles.organizationNameEdit}>
                                {isEditingOrgName ? (
                                    <div className={styles.editingContainer}>
                                        <input
                                            type="text"
                                            value={orgNameValue}
                                            onChange={(e) => setOrgNameValue(e.target.value)}
                                            onKeyDown={handleOrgNameKeyPress}
                                            className={styles.orgNameInput}
                                            autoFocus
                                        />
                                        <div className={styles.editButtons}>
                                            <button onClick={handleSaveOrgName} className={styles.saveButton}>Save</button>
                                            <button onClick={handleCancelOrgNameEdit} className={styles.cancelButton}>Cancel</button>
                                        </div>
                                    </div>
                                ) : (
                                    <div className={styles.displayContainer}>
                                        <span className={styles.orgNameDisplay}>{currentOrganization?.name}</span>
                                        <button onClick={handleEditOrgName} className={styles.editButton}>Edit</button>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                )}

                {/* Subscription section - visible to owners and admins */}
                {(currentUserRole === 'OWNER' || currentUserRole === 'ADMIN') && (
                    <SubscriptionSection currentOrganization={currentOrganization} />
                )}

                {/* Organization logo section - visible to owners and admins */}
                {(currentUserRole === 'OWNER' || currentUserRole === 'ADMIN') && (
                    <div className={styles.organizationDetailsSection}>
                        <div className={styles.organizationLogoSection}>
                            <h2>Organization Logo</h2>
                            <OrganizationLogo
                                logoURL={currentOrganization?.logo_url ? `${currentOrganization.logo_url}?${logoTimestamp}` : null}
                                uploadEnabled={false}
                                isUploadingLogo={isUploadingLogo}
                                upload={(image) => uploadOrganizationLogo(image)}
                            />
                        </div>
                    </div>
                )}


                <h1>Manage your coaching staff</h1>
                {currentOrganization?.id === 0 && (
                    <div style={{fontSize: '12px', color: '#ff6b6b', marginBottom: '10px', padding: '8px', backgroundColor: '#ffe0e0', borderRadius: '4px'}}>
                        ⚠️ Warning: Organization ID is 0 (should be UUID). Staff management may not work correctly.
                    </div>
                )}


                <p className={styles.description}>
                    All staff members can view athletes and programs, and create new athletes and programs for this organization.
                </p>

                <div className={styles.searchBox}>
                    <input
                        type="text"
                        placeholder="Search by name or email..."
                        value={searchTerm || ''}
                        onChange={handleSearchChange}
                    />
                </div>

                <div className={styles.tableWrapper}>
                    <table className={styles.staffTable}>
                        <thead className={styles.tableHeader}>
                            <tr>
                                <th></th>
                                <th>FULL NAME</th>
                                <th>ROLE</th>
                                <th></th>
                            </tr>
                        </thead>
                        <tbody>
                            {staffMembers?.map((member) => (
                                <tr
                                    key={member.account_id}
                                    className={`${styles.staffRow} ${canEditMember(member, currentUserRole) ? styles.clickable : ''}`}
                                    onClick={() => handleRowClick(member)}
                                >
                                    <td>
                                        <div className={styles.userInfo}>
                                            <div className={styles.avatar}>
                                                {member.photo ? (
                                                    <img
                                                        src={member.photo}
                                                        alt="Staff avatar"
                                                        className={styles.avatarImage}
                                                    />
                                                ) : (
                                                    getInitials(member.account?.firstName, member.account?.lastName)
                                                )}
                                            </div>
                                            <div className={styles.userDetails}>
                                                <div className={styles.userName}>
                                                    {member.account?.email || '-'}
                                                </div>
                                                {member.title && (
                                                    <div className={styles.userTitle}>
                                                        {member.title}
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    </td>
                                    <td>
                                        <span className={styles.roleText}>
                                            {(() => {
                                                const firstName = member.name || member.account?.firstName || '';
                                                const lastName = member.last_name || member.account?.lastName || '';
                                                const fullName = `${firstName} ${lastName}`.trim();
                                                return fullName || '-';
                                            })()}
                                        </span>
                                    </td>
                                    <td>
                                        <span className={styles.roleText}>
                                            {member.role === 'OWNER' ? 'Owner' : member.role === 'ADMIN' ? 'Admin' : 'Staff'}
                                        </span>
                                    </td>
                                    <td>
                                        {!member.account?.isCurrentUser && canModifyRole(member.role, currentUserRole, false) && (
                                            <button
                                                className={styles.removeButton}
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    handleRemoveUser(member.account_id);
                                                }}
                                                title="Remove user"
                                            >
                                                ✕
                                            </button>
                                        )}
                                    </td>
                                </tr>
                            ))}

                            {/* Pending Invites */}
                            {pendingInvites?.map((invite) => (
                                <tr key={`invite-${invite.id}`} className={styles.inviteRow}>
                                    <td>
                                        <div className={styles.userInfo}>
                                            <div className={styles.avatar}>
                                                <Email />
                                            </div>
                                            <div className={styles.userDetails}>
                                                <div className={styles.userName}>
                                                    {invite.email}
                                                </div>
                                                <div className={styles.userTitle}>
                                                    Invitation pending
                                                </div>
                                            </div>
                                        </div>
                                    </td>
                                    <td>
                                        <span className={styles.nameText}>-</span>
                                    </td>
                                    <td>
                                        <span className={styles.roleText}>
                                            {invite.role === 'OWNER' ? 'Owner' : invite.role === 'ADMIN' ? 'Admin' : 'Staff'}
                                        </span>
                                    </td>
                                    <td>
                                        <button
                                            className={styles.removeButton}
                                            onClick={() => handleCancelInvite(invite.id)}
                                            title="Cancel invitation"
                                        >
                                            ✕
                                        </button>
                                    </td>
                                </tr>
                            ))}

                        </tbody>
                    </table>
                </div>
                <div className={styles.addStaffContainer}>
                    <button
                        className={styles.addStaffButton}
                        onClick={() => setIsInviteModalOpen(true)}
                    >
                        + ADD STAFF
                    </button>
                </div>
            </div>

            <InviteStaffModal
                isOpen={isInviteModalOpen}
                onClose={() => setIsInviteModalOpen(false)}
                onInvite={handleInviteStaff}
                currentUserRole={currentUserRole}
                loading={loading}
                error={error}
            />

            <StaffProfileModal
                isOpen={isProfileModalOpen}
                onClose={handleProfileClose}
                onSave={handleProfileSave}
                onRemove={() => {
                    if (selectedMember) {
                        handleRemoveUser(selectedMember.account_id);
                        handleProfileClose();
                    }
                }}
                onPhotoUpload={handlePhotoUpload}
                member={selectedMember}
                currentUserRole={currentUserRole}
                loading={loading}
                error={error}
                isUploadingPhoto={isUploadingPhoto}
                canEdit={selectedMember ? canEditMember(selectedMember, currentUserRole) : false}
                canRemove={selectedMember && !selectedMember.account?.isCurrentUser && canModifyRole(selectedMember.role, currentUserRole, false)}
            />
        </div>
    );
};

export default StaffView;
